//
// Copyright (c) 2011-2024 by Curalate, Inc.
//

import { pollBulkUpdateStatus } from '@apis/gatewayMediaApi';
import { ManageLabelTaskStatus } from '@apis/gatewayMediaApiModel';
import { getAllLabels } from '@content-drawer/filters/labels/hooks/useAllLabelsQuery';
import { wait } from '@utils/wait';

const FIVE_SECONDS_TIMEOUT = 5000;
const MAX_ITERATION_COUNT = 20;

const recursivelyCheckIsLabelUpdatedOrDeleted = async (
  params: LabelChangeCompletionParameters,
) => {
  const iteration = params.iteration ?? 1;
  if (iteration >= MAX_ITERATION_COUNT) {
    // we will try to check for update/delete completion 20 times or return a failure
    return false;
  }
  const { label, newLabel } = params;
  const labelObjects = await getAllLabels();
  const allLabels = labelObjects.data.indexObjects.map(
    (object) => object.userTag,
  );
  if (
    allLabels.includes(label) ||
    (newLabel && !allLabels.includes(newLabel))
  ) {
    await wait(FIVE_SECONDS_TIMEOUT);
    return recursivelyCheckIsLabelUpdatedOrDeleted({
      ...params,
      iteration: iteration + 1,
    });
  } else {
    return true;
  }
};

const checkLabelUpdateAndDeleteStatus = async (
  params: LabelChangeCompletionParameters,
) => {
  const status = await pollBulkUpdateStatus(params.instanceId);
  const iteration = params.iteration ?? 1;
  if (iteration >= MAX_ITERATION_COUNT) {
    // we will try to check for update/delete completion 20 times or return a failure
    return false;
  }

  switch (status) {
    case ManageLabelTaskStatus.WAITING:
    case ManageLabelTaskStatus.SCHEDULED:
    case ManageLabelTaskStatus.EXECUTING:
      await wait(FIVE_SECONDS_TIMEOUT);
      return checkLabelUpdateAndDeleteStatus({
        ...params,
        iteration: iteration + 1,
      });
      break;
    case ManageLabelTaskStatus.COMPLETED:
      return recursivelyCheckIsLabelUpdatedOrDeleted({
        ...params,
        iteration: iteration + 1,
      });
      break;
    default:
      break;
  }
  return true;
};

export interface LabelChangeCompletionParameters {
  label: string;
  newLabel?: string;
  instanceId: number;
  iteration?: number;
}

export const checkIsLabelUpdatedOrDeleted = async (
  params: LabelChangeCompletionParameters,
) => {
  // Either start polling the joule instance or update the label data to not include the deleted label
  // gatewayMediaApi.deleteLabel returns -1 if there are less than 100 DTS items with the label
  // gatewayMediaApi.updateLabel returns -1 if there are less than 100 DTS items with the label
  if (params.instanceId === -1) {
    return recursivelyCheckIsLabelUpdatedOrDeleted(params);
  } else {
    return checkLabelUpdateAndDeleteStatus(params);
  }
};
